import { useState } from 'react'

import { ApiFailure } from '../../../app/errors'
import { outcomeOf } from '../../../app/ai-os-operation'
import {
  useAiModules,
  useDisableVerifiedLearningEntry,
  useRetryImprovementProposal,
  useCreateImprovementProposal,
  useImprovementProposal,
  useImprovementProposals,
  useModuleEvaluationRuns,
  useVerifiedLearningEntries,
} from '../../../app/queries'
import { CreateProposal } from './create'
import { ModuleEvolutionView } from './view'

/**
 * 项目屏「模块演进」那块的接线层。
 *
 * 屏里不出现 `fetch`，也不出现路径字符串——都在 `app/ai-os.ts` 与
 * `app/queries.ts` 里。这一层只做三件事：把 hook 接起来、管两个本地状态
 * （展开了哪一条、筛到哪个模块）、把重试透传下去。
 *
 * # 详情与证据是**展开时才取**的
 *
 * 列表那条接口已经带回了完整的提案（契约里 `ImprovementProposal` 是同一个 schema），
 * 但展开时仍然打一次 `GET /proposals/{id}`：**列表是分页的、可能是缓存的，
 * 而用户展开一条正是想看它现在是什么样**。多这一次请求换来的是
 * 「展开看到的就是此刻的真相」。
 *
 * 评估与学习项按**那条提案所属的模块**筛。它们在契约里挂在模块上不挂在提案上，
 * 所以这里筛的是 `module_id`——不是把整个项目的评估都倒出来让用户自己找。
 *
 * # 重试重取四条，不是一条
 *
 * 四条请求多半是一起坏的（同一个后端、同一次网络抖动）。只重取出错的那一条，
 * 用户得点四次；而重取全部的代价是几个已经好了的请求多走一遍缓存。
 */
export function ModuleEvolutionSection({ projectId }: { projectId: string }) {
  const [moduleId, setModuleId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [submittedAt, setSubmittedAt] = useState<number | undefined>(undefined)
  /**
   * 服务端回的是「我已经记过一次了」吗。
   *
   * 判据是**回来那条提案的 `created_at` 比这次提交早**——同键同载荷时
   * 服务端回的是第一次那条，它的创建时刻在我们点提交之前。
   * 这与「新记了一条」在 HTTP 上是同一个 201，**分不开的话，
   * 用户会以为自己成功提了两条**。
   */
  const [replayed, setReplayed] = useState(false)
  /** 上一次「再来一次」点完之后成了吗。给 `ai_os.retry.manual_ok` 那一句用。 */
  const [retrySucceeded, setRetrySucceeded] = useState(false)

  const modules = useAiModules(projectId)
  const proposals = useImprovementProposals(projectId, moduleId ? { moduleId } : {})
  const detail = useImprovementProposal(projectId, expandedId)

  // 展开那条属于哪个模块。取不到详情之前先用列表里那条——
  // **两者的 module_id 是同一个字段**，等详情回来再切换只会让证据那一块闪一下
  const expandedModuleId =
    detail.data?.module_id ??
    proposals.data?.items.find((p) => p.id === expandedId)?.module_id ??
    undefined

  const evaluations = useModuleEvaluationRuns(
    projectId,
    expandedModuleId ? { moduleId: expandedModuleId } : {},
  )
  const learning = useVerifiedLearningEntries(
    projectId,
    expandedModuleId ? { moduleId: expandedModuleId } : {},
  )
  /**
   * 模块体检面板用的那一份，**不带模块筛选**。
   *
   * 与上面那个 `evaluations` 只差一个筛选条件，但不能合成一份：
   * 那一份会随「展开哪条提案」变，而体检面板要的一直是整个项目。
   * 没展开提案时两者的 queryKey 相同，TanStack Query 直接复用同一份缓存，
   * 不会多打一次请求。
   */
  const healthEvaluations = useModuleEvaluationRuns(projectId, {})

  const create = useCreateImprovementProposal(projectId)

  /**
   * 两个受控写口。
   *
   * # 「正在跑」只认真实事件
   *
   * `retry.isPending` 是**我们这一次请求真的在路上**，那是真实事件。
   * 而 `operation_in_flight` 是**服务端说上一次还在跑**，那是另一件事，
   * 也是真实事件——它由 409 的 `code` 判出来，不是这边猜的。
   *
   * **这两样之外一律不造 busy。** 没有第三种「看起来像在跑」的状态：
   * 一个凭空的忙碌动效会让用户以为有件事正在发生，而实际上什么都没有，
   * 然后他会等下去。
   *
   * # 一次只跟一条
   *
   * `variables.proposalId` 是 TanStack Query 给的**这一次** mutation 的入参。
   * 用它对上是哪一条，而不是记一个「上次点的是谁」——后者在快速点两条时会串。
   */
  const retry = useRetryImprovementProposal(projectId)
  const disable = useDisableVerifiedLearningEntry(projectId)

  const retryTargetId = retry.variables?.proposalId
  const retryResult = outcomeOf(retry.data, retry.error)
  const disableTargetId = disable.variables?.entryId
  const disableResult = outcomeOf(disable.data, disable.error)

  const refetchAll = () => {
    setRetrySucceeded(false)
    void modules.refetch()
    void proposals.refetch().then((r) => {
      // 「这次成了」只在**真的成了**的时候说。不看结果就先说一句，
      // 是把「我点了」当成了「它好了」
      if (!r.error) setRetrySucceeded(true)
    })
    if (expandedId) {
      void detail.refetch()
      void evaluations.refetch()
      void learning.refetch()
    }
  }

  return (
    <ModuleEvolutionView
      proposals={proposals.data?.items ?? []}
      proposalsPending={proposals.isPending}
      proposalsError={proposals.error ?? undefined}
      proposalsRetryCount={proposals.failureCount}
      modules={modules.data?.items ?? []}
      modulesPending={modules.isPending}
      modulesError={modules.error ?? undefined}
      moduleId={moduleId}
      onModuleChange={(next) => {
        setModuleId(next)
        // 换了筛选，展开的那条多半已经不在列表里了。留着它的话，
        // 下面会挂着一块与列表对不上的详情
        setExpandedId(null)
      }}
      expandedId={expandedId}
      onToggleExpand={setExpandedId}
      detail={detail.data}
      detailPending={Boolean(expandedId) && detail.isPending}
      detailError={detail.error ?? undefined}
      healthEvaluations={healthEvaluations.data?.items ?? []}
      healthEvaluationsPending={healthEvaluations.isPending}
      healthEvaluationsError={healthEvaluations.error ?? undefined}
      evaluations={evaluations.data?.items ?? []}
      evaluationsPending={Boolean(expandedModuleId) && evaluations.isPending}
      evaluationsError={evaluations.error ?? undefined}
      learning={learning.data?.items ?? []}
      learningPending={Boolean(expandedModuleId) && learning.isPending}
      learningError={learning.error ?? undefined}
      onRetry={refetchAll}
      retrySucceeded={retrySucceeded}
      onRetryProposal={(vars) =>
        retry.mutate({
          proposalId: vars.proposalId,
          idempotencyKey: vars.idempotencyKey,
          input: {
            expected_version: vars.expectedVersion,
            // **红级重试必须带上那个已经批过的 Action。**
            // 少了它服务端 409 `red_needs_approval`——而这个口
            // 自己不会签发批准，界面也不许替它造一个
            ...(vars.actionId ? { action_id: vars.actionId } : {}),
          },
        })
      }
      retryPendingId={retry.isPending ? retryTargetId : undefined}
      retryOutcome={
        retryTargetId && retryResult
          ? {
              proposalId: retryTargetId,
              outcome: retryResult,
              // 后端按契约给了话就原样带下去。六种 409 里有四种没有专门的模板，
              // 而服务端那句是它按 docs/copy 渲染好的，比这边任何一条泛用的准
              serverMessage:
                retry.error instanceof ApiFailure ? retry.error.message : undefined,
            }
          : undefined
      }
      onDisableLearning={(vars) =>
        disable.mutate({
          entryId: vars.entryId,
          idempotencyKey: vars.idempotencyKey,
          input: { reason: vars.reason, expected_version: vars.expectedVersion },
        })
      }
      disablePendingId={disable.isPending ? disableTargetId : undefined}
      disableOutcome={
        disableTargetId && disableResult
          ? {
              entryId: disableTargetId,
              outcome: disableResult,
              serverMessage:
                disable.error instanceof ApiFailure ? disable.error.message : undefined,
            }
          : undefined
      }
      createSlot={
        <CreateProposal
          modules={modules.data?.items ?? []}
          pending={create.isPending}
          error={create.error ?? undefined}
          submittedAt={submittedAt}
          replayed={replayed}
          onSubmit={(vars) => {
            const sentAt = Date.now()
            create.mutate(vars, {
              // 成功之后换一把新的幂等键：下一条提案是另一次操作。
              // **失败时刻意什么都不做**——键留着，「再来一次」带的还是它
              onSuccess: (created) => {
                setReplayed(
                  Boolean(created.created_at) &&
                    new Date(created.created_at as string).getTime() < sentAt - 1_000,
                )
                setSubmittedAt(Date.now())
              },
            })
          }}
        />
      }
    />
  )
}
